import { KatanaPerpsOrderEventData } from '@katanaperps/katana-perps-sdk';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchUserFillsPaginated } from 'api/apiExchange';
import { transformOrderFillToApiFill } from 'api/apiUtils';
import { ApiFill } from 'api/types';
import { useWebsocketEventListener } from 'components/hooks';
import { updateMinedFillInArray } from 'components/trading/MyTrades/fillsFetcherHook';
import { useSelector } from 'reduxState/store';

import { minutes, seconds } from './utils';

function mergeNewFills(
  previousFills: ApiFill[] = [],
  updatedOrderData: KatanaPerpsOrderEventData
) {
  const tradesIdsSet = new Set(previousFills.map(trade => trade.fillId));
  const newFills = (updatedOrderData.fills || [])
    .filter(fill => !tradesIdsSet.has(fill.fillId))
    .map(fill => transformOrderFillToApiFill(updatedOrderData, fill));
  return [...newFills, ...previousFills].sort((a, b) => b.time - a.time);
}

export const useGlobalFillQueriesInvalidator = () => {
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const queryClient = useQueryClient();

  useWebsocketEventListener('orders', event => {
    const { data: updatedOrderData } = event;
    const { market } = updatedOrderData;
    if (!market) {
      console.error('Received order - no market', event);
      return;
    }
    if (updatedOrderData.fills?.some(fill => fill.type === 'liquidation')) {
      queryClient.invalidateQueries({ queryKey: ['fills'] });
      return;
    }
    queryClient.setQueryData<ApiFill[]>(
      ['fills', walletAddress, 'all-markets'],
      previousFills =>
        previousFills
          ? mergeNewFills(previousFills, updatedOrderData)
          : undefined // README P9-4765
    );
    queryClient.setQueryData<ApiFill[]>(
      ['fills', walletAddress, market],
      previousFills =>
        previousFills
          ? mergeNewFills(previousFills, updatedOrderData)
          : undefined // README P9-4765
    );
  });

  useWebsocketEventListener('webclient', message => {
    const settledEvent = message.data;
    if (settledEvent.event !== 'transaction_settled') {
      return;
    }
    if (settledEvent.action === 'executeTrade' && settledEvent.payload.market) {
      const market = settledEvent.payload.market;

      queryClient.setQueryData<ApiFill[]>(
        ['fills', walletAddress, market],
        previousFills =>
          previousFills
            ? updateMinedFillInArray(previousFills, settledEvent.payload)
            : undefined // README P9-4765
      );

      queryClient.setQueryData<ApiFill[]>(
        ['fills', walletAddress, 'all-markets'],
        previousFills =>
          previousFills
            ? updateMinedFillInArray(previousFills, settledEvent.payload)
            : undefined // README P9-4765
      );
    }
  });
};

/** marketPair=false means fetching of All-Market */
export const useMarketFillsQuery = (marketPair?: string | false) => {
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  return useQuery({
    queryKey: ['fills', walletAddress, marketPair || 'all-markets'],
    queryFn:
      !walletAddress || marketPair === undefined
        ? skipToken
        : async () => {
            // We have to use internal paginated API,
            // because it includes indexPrice for liquidations
            const data = await fetchUserFillsPaginated(
              marketPair === false
                ? {
                    wallet: walletAddress,
                    isVault: false,
                    page: 1,
                    limit: 100,
                  }
                : {
                    wallet: walletAddress,
                    isVault: false,
                    page: 1,
                    limit: 100,
                    market: marketPair,
                  }
            );
            return data.items;
          },
    staleTime: seconds(15), // We rely on "useGlobalFillQueriesInvalidator"
    refetchOnWindowFocus: false,
    refetchInterval: minutes(5),
  });
};
